import { ApiError, describeApiError } from '../api/authFetch';

// Shared by AdminDashboardV2.js and the admin panels extracted out of it
// (UsersView.js and friends) — moved here rather than duplicated so every
// caller normalizes FastAPI/Pydantic validation errors the same way.

// Known invitation-payload field keys mapped to human-readable labels, used
// by formatErrorDetail below so admins see "Email" / "School" / "Role"
// instead of raw schema field names. Any field not in this map falls back
// to its raw key rather than being hidden.
const FIELD_LABELS = { email: 'Email', school: 'School', role: 'Role' };

// FastAPI/Pydantic validation errors return `detail` as an array of
// `{ loc, msg, type }` objects (occasionally a plain object) rather than a
// string. Rendering that directly as a React child (e.g. inside <Alert>)
// throws "Objects are not valid as a React child" — this normalizes any
// shape of `detail` down to a display string first.
//
// For bulk requests, `loc` looks like `["body", "invitations", 2, "email"]`
// — the numeric segment is the failing row's index. We surface that as a
// human 1-indexed "Row N" reference so two failing rows don't collapse into
// two identical "email: ..." fragments; the `body -> field` path itself is
// still not leaked.
export const formatErrorDetail = (detail, fallback = 'Please try again.') => {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const messages = detail.map(item => {
      if (typeof item === 'string') return item;
      const loc = Array.isArray(item?.loc) ? item.loc : [];
      const fieldKey = loc.length ? loc[loc.length - 1] : null;
      const fieldLabel = fieldKey != null ? (FIELD_LABELS[fieldKey] || fieldKey) : null;
      const rowIndex = loc.find(seg => typeof seg === 'number');
      const rowPrefix = typeof rowIndex === 'number' ? `Row ${rowIndex + 1} — ` : '';
      const msg = item?.msg || JSON.stringify(item);
      return fieldLabel ? `${rowPrefix}${fieldLabel}: ${msg}` : `${rowPrefix}${msg}`;
    });
    return messages.join('; ') || fallback;
  }
  if (typeof detail === 'object') {
    return detail.msg || JSON.stringify(detail);
  }
  return String(detail);
};

// Composes describeApiError() (authFetch.js) — network errors, timeouts,
// opaque 5xxs — with formatErrorDetail()'s structured-validation handling.
// authFetch's ApiError carries the backend's raw `detail`, and for a
// FastAPI/Pydantic 422 that's an array of `{ loc, msg, type }` objects, not a
// string; describeApiError() has no way to know that shape, so route
// ApiError.detail through formatErrorDetail() first and only fall back to
// describeApiError()'s generic message when there's no structured detail to
// work with (network failure, timeout, unhandled 500).
export const describeAdminError = (error, fallback) => {
  if (error instanceof ApiError && error.detail) {
    return formatErrorDetail(error.detail, fallback ?? describeApiError(error));
  }
  return describeApiError(error);
};
